import { Alert, Badge, Button, Flex, Group, Paper, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useGetOperationalReturnOrders } from '../../../hooks/orders/queries';
import { IOrder } from '../../../interfaces/Order';
import { buildOrderFolio } from '../../../utils/ordersUtils';
import { formatMoneyString } from '../../../utils/strings';

const getEntityId = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && '_id' in value) {
    return String((value as { _id?: string })._id ?? '');
  }
  return '';
};

const EditOrderHeaderAlert = ({
  order,
  sourceOrder
}: {
  order?: IOrder;
  sourceOrder?: IOrder;
}) => {
  const { t } = useTranslation(['NewOrder', 'CommonButtons', 'FieldName']);
  const history = useHistory();
  const [hideAlert, setHideAlert] = useState(false);
  const isReturn = order?.orderType === 'return';
  const { data: linkedReturns = [] } = useGetOperationalReturnOrders(order?._id || '', {
    enabled: Boolean(order?._id) && !isReturn
  });

  const goToOrder = (targetOrder: IOrder) => {
    if (!targetOrder._id) return;
    history.push(`/orders/${targetOrder._id}?storeId=${getEntityId(targetOrder.createdIn)}`);
  };

  const getStatusLabel = (status?: string) => {
    return status ? t(`FieldName:orderStatuses.${status}`) : '';
  };

  if (hideAlert || !order) return null;
  const folio = buildOrderFolio(order.folioPrefix, order.sequence);
  return (
    <Alert
      onClose={() => setHideAlert(true)}
      withCloseButton
      title={
        <Flex justify="space-between" align="center" gap="sm">
          <Group gap="xs">
            {isReturn ? (
              <Badge color="blue" variant="filled">
                {t('NewOrder:operationalReturns.badge')}
              </Badge>
            ) : null}
            <Text fw={500}>
              {t('NewOrder:updatingOrder')}: <b>{folio}</b>
            </Text>
          </Group>
          <Button variant="light" color="red" onClick={() => history.goBack()}>
            {t('CommonButtons:cancel')}
          </Button>
        </Flex>
      }
      variant="light"
      color={isReturn ? 'cyan' : 'blue'}>
      <Stack gap="sm">
        {isReturn ? (
          <>
            <Text size="sm" fw={500}>
              {t(
                order.returnItemsChargeMode === 'charge'
                  ? 'NewOrder:operationalReturns.chargeModeCharge'
                  : 'NewOrder:operationalReturns.chargeModeNoCharge'
              )}
            </Text>
            {sourceOrder ? (
              <Button variant="subtle" size="compact-sm" onClick={() => goToOrder(sourceOrder)}>
                {t('NewOrder:operationalReturns.sourceRelationship', {
                  folio: buildOrderFolio(sourceOrder.folioPrefix, sourceOrder.sequence)
                })}
              </Button>
            ) : null}
          </>
        ) : (
          <Text size="sm">
            {t('NewOrder:editOrderPaymentWarning')}
          </Text>
        )}

        {!isReturn && linkedReturns.length > 0 ? (
          <Stack gap="xs">
            <Text fw={600} size="sm">
              {t('NewOrder:operationalReturns.linkedReturns')}
            </Text>
            {linkedReturns.map((linkedReturn) => (
              <Paper key={linkedReturn._id} withBorder p="xs">
                <Group justify="space-between" wrap="nowrap">
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {buildOrderFolio(linkedReturn.folioPrefix, linkedReturn.sequence)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {getStatusLabel(linkedReturn.status)} · {formatMoneyString(linkedReturn.total)}
                    </Text>
                  </Stack>
                  <Button variant="subtle" size="compact-xs" onClick={() => goToOrder(linkedReturn)}>
                    {t('CommonButtons:view')}
                  </Button>
                </Group>
              </Paper>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Alert>
  );
};

export default EditOrderHeaderAlert;
